import React, { FunctionComponent, HTMLAttributes } from 'react'
import clsx from 'clsx'

import { TEXT_TAGS } from './constants'
import styles from './style.module.scss'
import { useDesignLanguagePerClass, useIsDesignLanguage } from '../Theme'
import { DesignLanguage } from '../Theme/types'
import { ColorTypes } from '../foundation/Colors/constants'
import { getColorClassName } from '../foundation/Colors/utils'
import { TextSizes, TextTags, TextLineClamp, TypoFontFace } from './types'

type CSSClasses = keyof typeof styles

/*
  NOTE:
  Text size is currently being inherited by default.
  This can change in the future if we want it's usage to be more strict
*/

export interface TextProps extends HTMLAttributes<HTMLElement> {
  size?: TextSizes
  bold?: boolean
  renderAs?: TextTags
  strikethrough?: boolean
  underline?: boolean
  testNameSpace?: string
  color?: ColorTypes
  mintBar?: boolean
  lineClamp?: TextLineClamp
  fontFace?: TypoFontFace
}

export type TextComponent = FunctionComponent<TextProps>

const BaseText: TextComponent = ({
  size,
  bold = false,
  strikethrough = false,
  renderAs = TEXT_TAGS.DEFAULT,
  testNameSpace = '',
  children,
  color,
  mintBar = false,
  underline = false,
  lineClamp = 0,
  className,
  fontFace,
  ...otherProps
}) => {
  const designLanguageStyles = useDesignLanguagePerClass(styles)
  const isArcade = useIsDesignLanguage(DesignLanguage.Arcade)

  if (typeof children === 'undefined') {
    return null
  }

  // CashSansMono is Arcade-only, ignore it for other design languages
  const effectiveFontFace = fontFace === 'CashSansMono' && !isArcade ? undefined : fontFace

  const HTMLTag = renderAs
  const sizeClassName = typeof size !== 'undefined' && styles[`size${size}` as CSSClasses]
  const colorClassName = typeof color !== 'undefined' && styles[getColorClassName(color) as CSSClasses]
  const textClassName = clsx(
    styles.text,
    sizeClassName,
    colorClassName,
    bold && styles.bold,
    strikethrough && styles.strikethrough,
    mintBar && styles.mintBar,
    underline && styles.underline,
    lineClamp !== 0 && styles[`lineClamp${lineClamp}`],
    effectiveFontFace && styles[`fontFace${effectiveFontFace}`],
    className,
    designLanguageStyles.text
  )

  return (
    <HTMLTag className={textClassName} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-text` })} {...otherProps}>
      {children}
    </HTMLTag>
  )
}

export const Text: TextComponent = BaseText

export const P: TextComponent = ({ children, ...otherProps }) => (
  <Text {...otherProps} renderAs={TEXT_TAGS.PARAGRAPH}>
    {children}
  </Text>
)

export const Pre: TextComponent = ({ children, ...otherProps }) => (
  <Text {...otherProps} renderAs={TEXT_TAGS.PRE}>
    {children}
  </Text>
)

export const Span: TextComponent = ({ children, ...otherProps }) => (
  <Text {...otherProps} renderAs={TEXT_TAGS.SPAN}>
    {children}
  </Text>
)

export const Sub: TextComponent = ({ children, ...otherProps }) => (
  <Text {...otherProps} renderAs={TEXT_TAGS.SUB}>
    {children}
  </Text>
)

export const Sup: TextComponent = ({ children, ...otherProps }) => (
  <Text {...otherProps} renderAs={TEXT_TAGS.SUP}>
    {children}
  </Text>
)
